import React, { useState, useCallback } from 'react';
import { View, Text, Platform, Pressable, FlatList, ScrollView, Alert } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';
import { XStack, YStack, H4, Paragraph } from 'tamagui';
import { MaterialCommunityIcons, AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import useGlobalUser from '@hooks/getGlobalUser';
import apis from '@api/api';
import { useDispatch } from 'react-redux';
import { SHOW_AUTH_MODAL, SHOW_LOADING_MODAL, HIDE_LOADING_MODAL } from '@reduxLocal/actions/types';
import DefaultScreen from '@components/Global/DefaultScreenView';

interface Dashboard {
  _id: string;
  title: string;
  description?: string;
  dataSourceIds: any[];
  components: any[];
  createdAt: string;
  updatedAt: string;
}

const CustomDashboardsScreen = () => {
  const theme = themeSelector();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const userId = useGlobalUser();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: dashboardsResponse, isLoading, refetch } = useQuery({
    queryKey: ['userDashboards', userId],
    queryFn: async () => {
      if (!userId) return null;
      return await apis.getUserDashboards(userId, { isArchived: false });
    },
    enabled: !!userId,
  });

  const dashboards: Dashboard[] = dashboardsResponse?.data || [];

  const filteredDashboards = dashboards.filter(dashboard =>
    dashboard.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    dashboard.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = useCallback(async (dashboardId: string, dashboardTitle: string) => {
    Alert.alert(
      'Delete Dashboard',
      `Are you sure you want to delete "${dashboardTitle}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Deleting dashboard...' } });
              await apis.deleteDashboard(dashboardId);
              dispatch({ type: HIDE_LOADING_MODAL });
              dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Dashboard deleted successfully', type: 'success' } });
              queryClient.invalidateQueries({ queryKey: ['userDashboards'] });
            } catch (error: any) {
              dispatch({ type: HIDE_LOADING_MODAL });
              dispatch({ type: SHOW_AUTH_MODAL, data: { content: `Failed to delete dashboard: ${error.message}`, type: 'error' } });
            }
          }
        }
      ]
    );
  }, [dispatch, queryClient]);

  const handleEdit = useCallback((dashboardId: string) => {
    (navigation as any).navigate('DashboardEditor', { dashboardId, mode: 'edit' });
  }, [navigation]);

  const handleView = useCallback((dashboardId: string) => {
    (navigation as any).navigate('DashboardEditor', { dashboardId, mode: 'view' });
  }, [navigation]);

  const cardColors = {
    background: theme ? '#ffffff' : '#374151',
    border: theme ? '#e5e7eb' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    accent: '#3b82f6',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
  };

  const renderDashboardCard = useCallback(({ item }: { item: Dashboard }) => (
    <Pressable
      onPress={() => handleView(item._id)}
      style={{
        backgroundColor: cardColors.background,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: cardColors.border,
        padding: 16,
        margin: 8,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
        minHeight: 150,
      }}
    >
      <XStack alignItems="flex-start" justifyContent="space-between" marginBottom={12}>
        <YStack flex={1}>
          <H4 color={cardColors.text} marginBottom={4}>
            {item.title}
          </H4>
          {item.description && (
            <Paragraph color={cardColors.textSecondary} fontSize={12} numberOfLines={2}>
              {item.description}
            </Paragraph>
          )}
        </YStack>
        <XStack gap={8}>
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              handleEdit(item._id);
            }}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: cardColors.surface,
            }}
          >
            <AntDesign name="edit" size={16} color={cardColors.accent} />
          </Pressable>
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              handleDelete(item._id, item.title);
            }}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: cardColors.surface,
            }}
          >
            <AntDesign name="delete" size={16} color="#ef4444" />
          </Pressable>
        </XStack>
      </XStack>

      <XStack alignItems="center" gap={16} marginTop={12}>
        <XStack alignItems="center" gap={4}>
          <MaterialCommunityIcons name="database" size={16} color={cardColors.textSecondary} />
          <Text style={{ fontSize: 12, color: cardColors.textSecondary }}>
            {item.dataSourceIds?.length || 0} {item.dataSourceIds?.length === 1 ? 'source' : 'sources'}
          </Text>
        </XStack>
        <XStack alignItems="center" gap={4}>
          <MaterialCommunityIcons name="view-grid" size={16} color={cardColors.textSecondary} />
          <Text style={{ fontSize: 12, color: cardColors.textSecondary }}>
            {item.components?.length || 0} {item.components?.length === 1 ? 'component' : 'components'}
          </Text>
        </XStack>
        <Text style={{ fontSize: 12, color: cardColors.textSecondary }}>
          {new Date(item.updatedAt || item.createdAt).toLocaleDateString()}
        </Text>
      </XStack>
    </Pressable>
  ), [theme, cardColors, handleView, handleEdit, handleDelete]);

  return (
    <DefaultScreen>
      <View style={{ flex: 1, padding: 16, backgroundColor: theme ? '#f9fafb' : '#1f2937' }}>
        <XStack alignItems="center" justifyContent="space-between" marginBottom={20}>
          <H4 color={cardColors.text}>Custom Dashboards</H4>
          <XStack gap={8}>
            <Pressable
              onPress={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              style={{
                padding: 8,
                borderRadius: 6,
                backgroundColor: cardColors.surface,
              }}
            >
              <MaterialCommunityIcons
                name={viewMode === 'grid' ? 'view-list' : 'view-grid'}
                size={20}
                color={cardColors.text}
              />
            </Pressable>
            <Pressable
              onPress={() => (navigation as any).navigate('DashboardEditor', { mode: 'create' })}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: cardColors.accent,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <AntDesign name="plus" size={16} color="white" />
              <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                New Dashboard
              </Text>
            </Pressable>
          </XStack>
        </XStack>

        {isLoading ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <Text style={{ color: cardColors.textSecondary }}>Loading dashboards...</Text>
          </View>
        ) : filteredDashboards.length === 0 ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
            <MaterialCommunityIcons name="view-dashboard-outline" size={64} color={cardColors.textSecondary} />
            <Text style={{ color: cardColors.text, fontSize: 18, fontWeight: '600', marginTop: 16, marginBottom: 8 }}>
              No dashboards yet
            </Text>
            <Text style={{ color: cardColors.textSecondary, fontSize: 14, textAlign: 'center', marginBottom: 24 }}>
              {searchQuery ? 'No dashboards match your search.' : 'Create your first dashboard to get started.'}
            </Text>
            {!searchQuery && (
              <Pressable
                onPress={() => (navigation as any).navigate('DashboardEditor', { mode: 'create' })}
                style={{
                  paddingHorizontal: 24,
                  paddingVertical: 12,
                  borderRadius: 8,
                  backgroundColor: cardColors.accent,
                }}
              >
                <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                  Create Dashboard
                </Text>
              </Pressable>
            )}
          </View>
        ) : (
          <FlatList
            data={filteredDashboards}
            renderItem={renderDashboardCard}
            keyExtractor={(item) => item._id}
            numColumns={viewMode === 'grid' ? (Platform.OS === 'web' ? 3 : 2) : 1}
            key={viewMode}
            contentContainerStyle={{ paddingBottom: 20 }}
            refreshing={isLoading}
            onRefresh={refetch}
          />
        )}
      </View>
    </DefaultScreen>
  );
};

export default CustomDashboardsScreen;






